import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

import type { TeamMessageRecord } from "../registration/registrationModelV4";
import { hasProfileValue, problemCategoryOf, problemValueOf } from "./screenModelV4";

// ---------- flag แจ้งเตือน ----------

export type FlagKey = "src" | "req" | "alg" | "chr" | "med" | "sur";

export type PatientFlag = {
  key: FlagKey;
  label: string;
  glyph: string;
  off?: boolean;
  text: string;
  /** รายการแพ้ล่าสุด (ผู้บันทึก / เวลา) */
  adr?: { edit_user?: string; edited?: string };
  recordedBy?: string | null;
  recordedAt?: string | null;
};

export type FlagSource = {
  patientADR?: any;
  problemListDPO?: any;
  onEditFlags?: () => void;
  channels?: string[];
  latestChannel?: TeamMessageRecord;
  specialRequests?: TeamMessageRecord;
  medications?: string[];
};

/**
 * แถวของ ProblemList ที่หน้าคัดกรองบันทึกไว้ ขึ้นต้นด้วยหมวดหมู่
 * (โรคประจำตัว / ยาที่ใช้ประจำ / ประวัติผ่าตัด) -> ปุ่มแจ้งเตือนแสดงเฉพาะค่าจริง
 * ไม่โชว์คำขึ้นต้น และคำที่ขึ้นต้นด้วย "ไม่มี" ถือว่าไม่มีข้อมูล
 */
export type ProblemRow = { category: string; value: string };

/** flag ที่มีแหล่งข้อมูล เรียงตามแบบ */
export const FLAG_DEFS: PatientFlag[] = [
  { key: "src", label: "ช่องทางที่รู้จักคลินิก", glyph: "i", text: "" },
  { key: "req", label: "Request พิเศษ", glyph: "!", text: "" },
  { key: "alg", label: "ประวัติแพ้", glyph: "แพ้", text: "" },
  { key: "chr", label: "โรคประจำตัว", glyph: "โรค", text: "" },
  { key: "med", label: "ยาที่ใช้ประจำ", glyph: "ยา", text: "" },
  /* issue 86615 ข้อ 8: ประวัติผ่าตัดมีปุ่มแจ้งเตือนด้วย */
  { key: "sur", label: "ประวัติผ่าตัด", glyph: "ผ่า", text: "" },
];

export const problemRowsOf = (source: any): ProblemRow[] =>
  (((source && source.items) || []) as any[])
    .filter((item: any) => item && item.is_active !== false)
    .map((item: any) => ({
      category: problemCategoryOf(String(item.message ?? "")),
      value: problemValueOf(String(item.message ?? "")),
    }))
    .filter((row: ProblemRow) => hasProfileValue(row.value));

/** แสดงเฉพาะ flag ที่มีข้อมูล (แพ้ / โรค ยังแสดงเมื่อแก้ไขได้ เพื่อเปิด editor) */
export const flagsOf = (props: FlagSource): PatientFlag[] => {
  const { patientADR, problemListDPO } = props;
  const problemRows = problemRowsOf(problemListDPO);

  const result: PatientFlag[] = [];
  for (const flag of FLAG_DEFS) {
    const record = flag.key === "src" ? props.latestChannel : flag.key === "req" ? props.specialRequests : undefined;
    if (record?.items?.length) result.push({ ...flag, text: record.items.map((item) => item.name).join(" · "),
      recordedBy: record.recorded_by,
      recordedAt: record.recorded_at && /^\d{4}-\d{2}-\d{2}T/.test(record.recorded_at)
        ? formatISOtoBEDateTime(record.recorded_at).dateTimeShort : record.recorded_at });
    // channels remains a compatibility prop for mock callers, never filled from a live EN.
    if (flag.key === "src" && !props.latestChannel && props.channels?.length) {
      result.push({ ...flag, text: props.channels.join(", ") });
    }
    if (flag.key === "alg" && patientADR === null) {
      result.push({ ...flag, off: true, text: "กำลังโหลด…" });
    }
    if (flag.key === "alg" && patientADR) {
      const items = (patientADR.items || []).filter(
        (item: any) => item.status !== "NO_REACTION" && item.status !== "CANCELED",
      );
      const names = items.map((item: any) =>
        item.note ? `${item.name} (${item.note})` : item.name,
      );
      if (names.length || props.onEditFlags) {
        result.push({
          ...flag,
          off: !names.length,
          text: names.join(", ") || "ไม่มีประวัติแพ้",
          // ผู้บันทึกล่าสุด
          adr: items.reduce(
            (last: any, item: any) => ((item.id ?? 0) > (last.id ?? 0) ? item : last),
            items[0],
          ),
        });
      }
    }
    if (flag.key === "chr" && problemListDPO) {
      const names = problemRows.filter((row) => row.category === "disease").map((row) => row.value);
      if (names.length || props.onEditFlags) {
        result.push({
          ...flag,
          off: !names.length,
          text: names.join(", ") || "ไม่มีโรคประจำตัว",
        });
      }
    }
    if (flag.key === "med") {
      const names = [
        ...(props.medications || []),
        ...problemRows.filter((row) => row.category === "medication").map((row) => row.value),
      ].filter((text) => hasProfileValue(text));
      if (names.length) result.push({ ...flag, text: Array.from(new Set(names)).join(", ") });
    }

    if (flag.key === "sur") {
      const names = problemRows.filter((row) => row.category === "surgery").map((row) => row.value);
      if (names.length) result.push({ ...flag, text: names.join(", ") });
    }
  }
  return result.map((flag) => {
    const category = flag.key === "chr" ? "disease" : flag.key === "med" ? "medication" : flag.key === "sur" ? "surgery" : null;
    const rows = ((problemListDPO?.items ?? []) as any[]).filter((item) => item.is_active !== false && problemCategoryOf(String(item.message ?? "")) === category);
    const latest = rows.sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0];
    return latest ? { ...flag, recordedBy: latest.edit_user_name || latest.edit_user, recordedAt: latest.edited || latest.created } : flag;
  });
};

// ---------- ข้อความ ----------

export const numberText = (value: number): string => value.toLocaleString("en-US");
